import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { getDefaultWaybillSettings, resolveWaybillSettings } from '@hero/core';
import {
  defaultWaybillSettings,
  orgWaybillLogoRoundingSchema,
  orgWaybillLogoShapeSchema,
  orgWaybillLogoSizeSchema,
  orgWaybillSettingsSchema,
  type OrganizationProfile,
} from '@hero/schema';
import { CheckIcon } from '@hero/web-icons';
import { FormFieldCaption } from '../../../app/FormFieldCaption';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';
import { SettingSwitchRow } from '../../../app/SettingSwitchRow';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';

const waybillFormSchema = orgWaybillSettingsSchema.pick({
  accent_color: true,
  use_org_logo: true,
  logo_shape: true,
  logo_background: true,
  logo_size: true,
  logo_rounding: true,
});

type WaybillFormValues = z.infer<typeof waybillFormSchema>;

const WAYBILL_HELPER_DISPLAY = 'icon' as const;

export interface WaybillSettingsFormProps {
  profile: Partial<OrganizationProfile> | undefined;
  disabled?: boolean;
  isSubmitting?: boolean;
  onSubmit: (values: WaybillFormValues) => Promise<void>;
  onDraftChange?: (values: WaybillFormValues) => void;
}

export function WaybillSettingsForm({
  profile,
  disabled,
  isSubmitting,
  onSubmit,
  onDraftChange,
}: WaybillSettingsFormProps) {
  const { t } = useTranslation(['m-waybill', 'm-organization']);

  const defaultValues = useMemo<WaybillFormValues>(() => {
    return resolveWaybillSettings(profile?.businessSettings?.waybill_settings);
  }, [profile?.businessSettings?.waybill_settings, profile?._id]);

  const methods = useForm<WaybillFormValues>({
    resolver: zodResolver(waybillFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  const { handleSubmit, setValue, formState, control } = methods;
  const accentColor = useWatch({ control, name: 'accent_color' });
  const useOrgLogo = useWatch({ control, name: 'use_org_logo' });
  const logoShape = useWatch({ control, name: 'logo_shape' });
  const logoBackground = useWatch({ control, name: 'logo_background' });
  const logoSize = useWatch({ control, name: 'logo_size' });
  const logoRounding = useWatch({ control, name: 'logo_rounding' });
  const lastDraftRef = useRef<string>('');

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  useEffect(() => {
    if (!onDraftChange) return;

    const subscription = methods.watch(formValues => {
      const serialized = JSON.stringify(formValues);
      if (serialized === lastDraftRef.current) return;
      lastDraftRef.current = serialized;
      onDraftChange(formValues);
    });

    return () => subscription.unsubscribe();
  }, [methods, onDraftChange]);

  const logoShapeOptions: SelectAutoOption[] = [
    { value: 'square', label: t('m-waybill:form.logo_shape.option_square') },
    { value: 'rectangle', label: t('m-waybill:form.logo_shape.option_rectangle') },
  ];

  const logoSizeOptions: SelectAutoOption[] = [
    { value: 'small', label: t('m-waybill:form.logo_size.option_small') },
    { value: 'medium', label: t('m-waybill:form.logo_size.option_medium') },
    { value: 'large', label: t('m-waybill:form.logo_size.option_large') },
  ];

  const logoRoundingOptions: SelectAutoOption[] = [
    { value: 'none', label: t('m-waybill:form.logo_rounding.option_none') },
    { value: 'sm', label: t('m-waybill:form.logo_rounding.option_sm') },
    { value: 'md', label: t('m-waybill:form.logo_rounding.option_md') },
    { value: 'full', label: t('m-waybill:form.logo_rounding.option_full') },
  ];

  const handleResetToDefaults = () => {
    const defaults = getDefaultWaybillSettings();
    lastDraftRef.current = '';
    methods.reset(defaults, { keepDefaultValues: true });
    onDraftChange?.(defaults);
    lastDraftRef.current = JSON.stringify(defaults);
  };

  return (
    <FormProvider {...methods}>
      <form
        className="space-y-6"
        onSubmit={handleSubmit(async data => {
          await onSubmit(data);
        })}>
        <FormFieldCaption
          label={t('m-waybill:form.accent_color.title')}
          helper={t('m-waybill:form.accent_color.helper')}
          helperDisplay={WAYBILL_HELPER_DISPLAY}
          htmlFor="waybill-accent-color">
          <div className="flex items-center gap-3">
            <input
              id="waybill-accent-color"
              type="color"
              disabled={disabled}
              value={accentColor ?? defaultWaybillSettings.accent_color}
              onChange={e => setValue('accent_color', e.target.value, { shouldDirty: true })}
              className="h-10 w-14 cursor-pointer rounded border border-border bg-background p-1"
            />
            <input
              type="text"
              disabled={disabled}
              value={accentColor ?? ''}
              onChange={e => setValue('accent_color', e.target.value, { shouldDirty: true })}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              placeholder="#2563EB"
            />
          </div>
        </FormFieldCaption>

        <SettingSwitchRow
          id="waybill-use-logo"
          label={t('m-waybill:form.use_org_logo.title')}
          helper={t('m-waybill:form.use_org_logo.helper')}
          helperDisplay={WAYBILL_HELPER_DISPLAY}
          disabled={disabled}
          checked={useOrgLogo ?? defaultWaybillSettings.use_org_logo}
          onCheckedChange={checked => setValue('use_org_logo', checked, { shouldDirty: true })}
        />

        {useOrgLogo && (
          <>
            <SelectAuto
              label={t('m-waybill:form.logo_shape.title')}
              helperText={t('m-waybill:form.logo_shape.helper')}
              helperDisplay={WAYBILL_HELPER_DISPLAY}
              options={logoShapeOptions}
              value={logoShape ?? defaultWaybillSettings.logo_shape}
              onChange={value =>
                setValue('logo_shape', orgWaybillLogoShapeSchema.parse(value), {
                  shouldDirty: true,
                })
              }
              disabled={disabled}
            />

            <SelectAuto
              label={t('m-waybill:form.logo_size.title')}
              helperText={t('m-waybill:form.logo_size.helper')}
              helperDisplay={WAYBILL_HELPER_DISPLAY}
              options={logoSizeOptions}
              value={logoSize ?? defaultWaybillSettings.logo_size}
              onChange={value =>
                setValue('logo_size', orgWaybillLogoSizeSchema.parse(value), {
                  shouldDirty: true,
                })
              }
              disabled={disabled}
            />

            <SelectAuto
              label={t('m-waybill:form.logo_rounding.title')}
              helperText={t('m-waybill:form.logo_rounding.helper')}
              helperDisplay={WAYBILL_HELPER_DISPLAY}
              options={logoRoundingOptions}
              value={logoRounding ?? defaultWaybillSettings.logo_rounding}
              onChange={value =>
                setValue('logo_rounding', orgWaybillLogoRoundingSchema.parse(value), {
                  shouldDirty: true,
                })
              }
              disabled={disabled}
            />

            <SettingSwitchRow
              id="waybill-logo-background"
              label={t('m-waybill:form.logo_background.title')}
              helper={t('m-waybill:form.logo_background.helper')}
              helperDisplay={WAYBILL_HELPER_DISPLAY}
              disabled={disabled}
              checked={
                (logoBackground ?? defaultWaybillSettings.logo_background) === 'white'
              }
              onCheckedChange={checked =>
                setValue('logo_background', checked ? 'white' : 'none', { shouldDirty: true })
              }
            />
          </>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={disabled || isSubmitting || !formState.isDirty}>
            {isSubmitting ? <Spinner /> : <CheckIcon className="w-4 h-4 me-2" />}
            {t('m-organization:buttons.save')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || isSubmitting}
            onClick={handleResetToDefaults}>
            {t('m-waybill:buttons.reset_to_defaults')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
